import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ExternalLayout } from "@/components/ExternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { CUSTOMER_PHASE_LABELS } from "@/lib/constants";
import { ChevronRight, Eye } from "lucide-react";

export default function CustomerPortal() {
  const [projects, setProjects] = useState([]);
  const { isInternal } = useAuth();
  const previewMode = isInternal();

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  return (
    <ExternalLayout>
      {previewMode && (
        <div
          data-testid="customer-portal-preview-banner"
          className="mb-6 border border-amber-300 bg-amber-50 rounded-md px-4 py-3 flex items-start gap-3"
        >
          <Eye className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">
            <div className="font-medium">Vorschau · Kundensicht</div>
            <div className="mt-0.5">
              Sie sehen das Kundenportal als interne Hospitanz. Aktionen sind in dieser Ansicht nicht möglich.
            </div>
          </div>
        </div>
      )}

      <div className="mb-10" data-testid="customer-portal-page">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Ihre Projekte</div>
        <h1 className="font-display text-3xl md:text-4xl text-neutral-900 leading-tight">
          Willkommen.
        </h1>
        <p className="text-neutral-700 mt-3 leading-relaxed max-w-prose">
          Hier sehen Sie den Stand Ihrer Projekte – ruhig, klar und nachvollziehbar.
        </p>
      </div>

      <div className="space-y-3">
        {projects.length === 0 && (
          <div className="border border-neutral-200 rounded-md p-8 text-center text-neutral-500" data-testid="customer-portal-empty">
            Aktuell sind keine Projekte mit Ihrem Konto verknüpft.
          </div>
        )}
        {projects.map((p) => (
          <Link
            to={`/portal/${p.project_id}`}
            key={p.project_id}
            data-testid={`customer-project-${p.project_id}`}
            className="block border border-neutral-200 rounded-md p-6 md:p-8 hover:border-neutral-400 transition-colors group bg-white"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">{p.customer_org}</div>
                <h2 className="font-display text-xl md:text-2xl text-neutral-900 group-hover:text-[#183153]">{p.name}</h2>
              </div>
              <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-[#183153] mt-1 flex-shrink-0" />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
              <StatusPill kind="customer_phase" value={p.customer_phase} />
              {p.sign_off_summary && (
                <span className="font-mono text-[11px] text-emerald-700">Freigegeben</span>
              )}
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-12 p-6 border border-neutral-200 rounded-md">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-2">Phasen-Modell</div>
        <p className="text-sm text-neutral-700 leading-relaxed mb-4">
          Wir führen jedes Projekt durch fünf klar definierte Phasen.
        </p>
        <ol className="space-y-2">
          {Object.entries(CUSTOMER_PHASE_LABELS).map(([k, v], i) => (
            <li key={k} className="flex items-center gap-3 text-sm">
              <span className="inline-flex items-center justify-center w-6 h-6 font-mono text-xs bg-neutral-100 text-neutral-700">{i + 1}</span>
              <span className="text-neutral-900">{v}</span>
            </li>
          ))}
        </ol>
      </div>
    </ExternalLayout>
  );
}
